// Student DashboardPage — welcome heading + active-enrollment overview
// (existing, unchanged), plus: a next-session card, an outstanding-payments
// alert, and a two-column grid of recent attendance + certificate progress.
import { useMemo } from "react";
import { useAuth } from "../../hooks/useAuth.js";
import { useFetch } from "../../hooks/useApi.js";
import { useCourses } from "../../contexts/CoursesContext.js";
import StatCard from "../../components/shared/StatCard.js";
import Badge from "../../components/shared/Badge.js";
import NextSessionCard from "../../components/student/NextSessionCard.js";
import PaymentAlertsCard from "../../components/student/PaymentAlertsCard.js";
import RecentAttendanceCard from "../../components/student/RecentAttendanceCard.js";
import CertificateProgressCard from "../../components/student/CertificateProgressCard.js";
import type { CourseProgress } from "../../lib/studentDashboardTypes.js";
import type {
  AttendanceRecordWithSession,
  Course,
  Enrollment,
  PaginatedResult,
  PaymentWithCourse,
  SessionWithCourse,
} from "@dm-lms/shared/types";

// Narrower than the shared Certificate type — GET /api/certificates/mine
// returns raw certificate rows, no downloadUrl (that's only ever computed
// on demand by GET /api/certificates/:id/download).
interface StudentCertificate {
  id: string;
  code: string;
  studentId: string;
  courseId: string;
  issuedAt: string;
  r2Key: string;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { data: enrollmentsData, isLoading, error } = useFetch<PaginatedResult<Enrollment>>(
    "/api/enrollments/mine?page=1&pageSize=100"
  );
  const { courses } = useCourses();

  // ---- New data for the four added cards ----
  const { data: sessionsData, isLoading: sessionsLoading } = useFetch<SessionWithCourse[]>("/api/sessions");
  const { data: paymentsData } = useFetch<PaymentWithCourse[]>("/api/payments/history");
  const { data: attendanceData, isLoading: attendanceLoading } = useFetch<PaginatedResult<AttendanceRecordWithSession>>(
    user ? `/api/attendance/student/${user.id}?page=1&pageSize=100` : null
  );
  const { data: certificatesData } = useFetch<StudentCertificate[]>("/api/certificates/mine");

  const courseById = useMemo(() => {
    const map = new Map<string, Course>();
    for (const course of courses) map.set(course.id, course);
    return map;
  }, [courses]);

  // "Active" = enrolled but not yet completed.
  const activeEnrollments = useMemo(
    () => (enrollmentsData?.items ?? []).filter((enrollment) => enrollment.completedAt === null),
    [enrollmentsData]
  );

  // ---- Next session: soonest upcoming session across all enrolled courses ----
  const nextSession = useMemo(() => {
    const enrolledCourseIds = new Set((enrollmentsData?.items ?? []).map((e) => e.courseId));
    const now = Date.now();
    const upcoming = (sessionsData ?? [])
      .filter((session) => enrolledCourseIds.has(session.courseId) && new Date(session.scheduledAt).getTime() > now)
      .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
    return upcoming[0] ?? null;
  }, [sessionsData, enrollmentsData]);

  // ---- Payment alerts: pending or failed payments ----
  const outstandingPayments = useMemo(
    () => (paymentsData ?? []).filter((payment) => payment.status === "pending" || payment.status === "failed"),
    [paymentsData]
  );

  // ---- Certificate progress: one row per enrolled course ----
  const courseProgress = useMemo<CourseProgress[]>(() => {
    const sessionCountByCourse = new Map<string, number>();
    const courseIdBySessionId = new Map<string, string>();
    for (const session of sessionsData ?? []) {
      sessionCountByCourse.set(session.courseId, (sessionCountByCourse.get(session.courseId) ?? 0) + 1);
      courseIdBySessionId.set(session.id, session.courseId);
    }

    const presentCountByCourse = new Map<string, number>();
    for (const record of attendanceData?.items ?? []) {
      if (record.status !== "present") continue;
      const courseId = courseIdBySessionId.get(record.sessionId);
      if (!courseId) continue;
      presentCountByCourse.set(courseId, (presentCountByCourse.get(courseId) ?? 0) + 1);
    }

    const certifiedCourseIds = new Set((certificatesData ?? []).map((cert) => cert.courseId));

    return (enrollmentsData?.items ?? []).map((enrollment) => ({
      courseId:          enrollment.courseId,
      courseTitle:       courseById.get(enrollment.courseId)?.title ?? "Course",
      totalSessions:     sessionCountByCourse.get(enrollment.courseId) ?? 0,
      attendedSessions:  presentCountByCourse.get(enrollment.courseId) ?? 0,
      completedAt:       enrollment.completedAt,
      certificateIssued: certifiedCourseIds.has(enrollment.courseId),
    }));
  }, [enrollmentsData, sessionsData, attendanceData, certificatesData, courseById]);

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Welcome, {user?.name ?? "Student"}</h1>

      <div className="mb-6">
        <NextSessionCard session={nextSession} isLoading={sessionsLoading} />
      </div>

      {outstandingPayments.length > 0 && (
        <div className="mb-6">
          <PaymentAlertsCard payments={outstandingPayments} />
        </div>
      )}

      {isLoading && <p className="text-dm-slate text-sm">Loading your courses…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!isLoading && !error && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <StatCard label="Active Courses" value={activeEnrollments.length} />
          </div>

          <h2 className="text-lg font-medium text-dm-navy mb-3">Your Courses</h2>
          {activeEnrollments.length === 0 ? (
            <p className="text-dm-slate text-sm">You are not enrolled in any active courses.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeEnrollments.map((enrollment) => {
                const course = courseById.get(enrollment.courseId);
                return (
                  <div key={enrollment.id} className="rounded-lg border border-gray-200 bg-white p-4">
                    <p className="font-medium text-dm-navy mb-2">{course?.title ?? "Course"}</p>
                    {course && <Badge tone="gray">{course.mode}</Badge>}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RecentAttendanceCard records={attendanceData?.items ?? []} isLoading={attendanceLoading} />
        <CertificateProgressCard progress={courseProgress} isLoading={isLoading} />
      </div>
    </div>
  );
}
